// src/features/tournament/components/TournamentParticipantModal.tsx

import React, {
  useState,
  type FormEvent,
} from 'react';

import {
  Coins,
  Loader2,
  Plus,
  Search,
  UserRound,
  X,
} from 'lucide-react';

import {
  addTournamentParticipant,
  searchTournamentUsers,
} from '../services/tournament.service';

import type {
  TournamentListItem,
  UserSearchField,
  UserSearchItem,
} from '../types/tournament.types';

import {
  formatSeed,
} from '../utils/tournament.utils';


type TournamentParticipantModalProps = {
  tournament:
    TournamentListItem;

  onClose: () => void;

  onAdded: () => Promise<void>;
};


const TournamentParticipantModal:
  React.FC<
    TournamentParticipantModalProps
  > = ({
    tournament,
    onClose,
    onAdded,
  }) => {
    const [
      searchField,
      setSearchField,
    ] =
      useState<UserSearchField>(
        'userName',
      );

    const [
      searchKeyword,
      setSearchKeyword,
    ] =
      useState('');

    const [
      searchResults,
      setSearchResults,
    ] =
      useState<UserSearchItem[]>(
        [],
      );

    const [
      isSearching,
      setIsSearching,
    ] =
      useState(false);

    const [
      searchError,
      setSearchError,
    ] =
      useState('');

    const [
      addingUserId,
      setAddingUserId,
    ] =
      useState<number | null>(
        null,
      );


    const handleSearch =
      async (
        event:
          FormEvent<HTMLFormElement>,
      ): Promise<void> => {
        event.preventDefault();

        const keyword =
          searchKeyword.trim();

        if (!keyword) {
          setSearchError(
            '검색어를 입력해주세요.',
          );

          setSearchResults(
            [],
          );

          return;
        }

        try {
          setIsSearching(
            true,
          );

          setSearchError(
            '',
          );

          const users =
            await searchTournamentUsers(
              searchField,
              keyword,
            );

          setSearchResults(
            users,
          );

          if (
            users.length ===
            0
          ) {
            setSearchError(
              '검색된 회원이 없습니다.',
            );
          }

        } catch (
          error
        ) {
          console.error(
            '회원 검색 실패:',
            error,
          );

          setSearchResults(
            [],
          );

          setSearchError(
            '회원 검색에 실패했습니다.',
          );

        } finally {
          setIsSearching(
            false,
          );
        }
      };


    const handleAdd =
      async (
        user:
          UserSearchItem,
      ): Promise<void> => {
        const displayName =
          user.userNickname
            ?.trim() ||
          user.userName
            ?.trim() ||
          user.userId;

        const point =
          user.point ?? 0;

        const tournamentPrice =
          tournament
            .tournamentPrice ??
          0;

        const remainingPoint =
          point -
          tournamentPrice;

        if (
          point <
          tournamentPrice
        ) {
          window.alert(
            [
              '보유 시드가 부족합니다.',
              '',
              `현재 시드: ${formatSeed(
                point,
              )}`,
              `필요 시드: ${formatSeed(
                tournamentPrice,
              )}`,
            ].join(
              '\n',
            ),
          );

          return;
        }


        const confirmed =
          window.confirm(
            [
              `${displayName}님을 참가자로 추가하시겠습니까?`,
              '',
              `대회: ${tournament.tournamentTitle}`,
              `현재 시드: ${formatSeed(
                point,
              )}`,
              `참가 시드: ${formatSeed(
                tournamentPrice,
              )}`,
              `참가 후 시드: ${formatSeed(
                remainingPoint,
              )}`,
            ].join(
              '\n',
            ),
          );


        if (!confirmed) {
          return;
        }


        try {
          setAddingUserId(
            user.id,
          );

          await addTournamentParticipant(
            tournament.tournamentId,
            {
              userId:
                user.id,
            },
          );

          await onAdded();

          onClose();

          window.alert(
            `${displayName}님이 참가자로 추가되었습니다.`,
          );

        } catch (
          error
        ) {
          console.error(
            '대회 참가자 추가 실패:',
            error,
          );

          window.alert(
            '참가자 추가에 실패했습니다.',
          );

        } finally {
          setAddingUserId(
            null,
          );
        }
      };


    return (
      <div
        className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-label="대회 참가자 추가"
        onMouseDown={(
          event,
        ) => {
          if (
            event.target ===
              event.currentTarget &&
            addingUserId ===
              null
          ) {
            onClose();
          }
        }}
      >

        <div className="max-h-[90vh] w-full overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:max-w-xl sm:rounded-3xl">

          <div className="flex items-start justify-between border-b border-gray-200 p-4 sm:p-5">

            <div className="min-w-0">

              <span className="text-xs font-bold text-main">
                SUPERADMIN
              </span>

              <h3 className="mt-1 text-lg font-bold text-gray-900">
                참가자 추가
              </h3>

              <p className="mt-1 truncate text-sm text-gray-500">
                {
                  tournament
                    .tournamentTitle
                }
              </p>

              <div className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600">

                <Coins
                  size={13}
                />

                참가 시드{' '}
                {formatSeed(
                  tournament
                    .tournamentPrice,
                )}

              </div>

            </div>


            <button
              type="button"
              onClick={
                onClose
              }
              disabled={
                addingUserId !==
                null
              }
              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40"
              aria-label="닫기"
            >
              <X
                size={20}
              />
            </button>

          </div>


          <div className="max-h-[calc(90vh-120px)] overflow-y-auto p-4 sm:p-5">

            <form
              onSubmit={
                handleSearch
              }
              className="space-y-3"
            >

              <div className="flex gap-2">

                <select
                  value={
                    searchField
                  }
                  onChange={(
                    event,
                  ) =>
                    setSearchField(
                      event.target
                        .value as UserSearchField,
                    )
                  }
                  className="h-11 shrink-0 rounded-xl border border-gray-300 bg-white px-3 text-sm font-semibold text-gray-700 outline-none focus:border-main"
                >

                  <option value="userName">
                    이름
                  </option>

                  <option value="userNickname">
                    닉네임
                  </option>

                  <option value="userId">
                    아이디
                  </option>

                  <option value="phoneNumber">
                    전화번호
                  </option>

                </select>


                <div className="relative min-w-0 flex-1">

                  <Search
                    size={17}
                    className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />

                  <input
                    type="text"
                    value={
                      searchKeyword
                    }
                    onChange={(
                      event,
                    ) => {
                      setSearchKeyword(
                        event.target.value,
                      );

                      setSearchError(
                        '',
                      );
                    }}
                    placeholder="회원 검색"
                    className="h-11 w-full rounded-xl border border-gray-300 bg-white pl-10 pr-3 text-sm outline-none focus:border-main focus:ring-2 focus:ring-main/10"
                  />

                </div>


                <button
                  type="submit"
                  disabled={
                    isSearching
                  }
                  className="flex h-11 shrink-0 items-center justify-center rounded-xl bg-gray-900 px-4 text-sm font-bold text-white disabled:opacity-50"
                >

                  {isSearching ? (
                    <Loader2
                      size={17}
                      className="animate-spin"
                    />
                  ) : (
                    '검색'
                  )}

                </button>

              </div>

            </form>


            {searchError && (

              <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-600">
                {searchError}
              </p>

            )}


            <div className="mt-4 space-y-3">

              {searchResults.map(
                (
                  user,
                ) => {
                  const adding =
                    addingUserId ===
                    user.id;

                  const point =
                    user.point ??
                    0;

                  const tournamentPrice =
                    tournament
                      .tournamentPrice ??
                    0;

                  const remainingPoint =
                    point -
                    tournamentPrice;

                  const hasEnoughPoint =
                    point >=
                    tournamentPrice;


                  return (
                    <div
                      key={
                        user.id
                      }
                      className="rounded-2xl border border-gray-200 bg-white p-3"
                    >

                      <div className="flex items-center gap-3">

                        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-gray-100 text-gray-500">
                          <UserRound
                            size={19}
                          />
                        </span>


                        <div className="min-w-0 flex-1">

                          <strong className="truncate text-sm text-gray-900">
                            {user.userNickname ||
                              user.userName}
                          </strong>


                          <p className="mt-0.5 truncate text-xs text-gray-500">
                            {user.userName}
                            {' · '}
                            {user.userId}
                          </p>


                          <p className="mt-0.5 truncate text-xs text-gray-400">
                            {user.phoneNumber ||
                              '전화번호 없음'}
                          </p>

                        </div>


                        <button
                          type="button"
                          onClick={() =>
                            void handleAdd(
                              user,
                            )
                          }
                          disabled={
                            addingUserId !==
                              null ||
                            !hasEnoughPoint
                          }
                          className="flex h-9 shrink-0 items-center gap-1.5 rounded-lg bg-main px-3 text-xs font-bold text-white disabled:opacity-40"
                        >
                          {adding ? (
                            <Loader2
                              size={14}
                              className="animate-spin"
                            />
                          ) : (
                            <Plus
                              size={14}
                            />
                          )}

                          추가
                        </button>

                      </div>


                      <div className="mt-3 grid grid-cols-3 gap-2 border-t border-gray-100 pt-3">

                        <div className="rounded-xl bg-gray-50 px-3 py-2">

                          <p className="text-[11px] text-gray-400">
                            보유 시드
                          </p>

                          <p className="mt-0.5 text-sm font-bold">
                            {formatSeed(
                              point,
                            )}
                          </p>

                        </div>


                        <div className="rounded-xl bg-gray-50 px-3 py-2">

                          <p className="text-[11px] text-gray-400">
                            참가 시드
                          </p>

                          <p className="mt-0.5 text-sm font-bold">
                            {formatSeed(
                              tournamentPrice,
                            )}
                          </p>

                        </div>


                        <div
                          className={`rounded-xl px-3 py-2 ${
                            hasEnoughPoint
                              ? 'bg-emerald-50'
                              : 'bg-red-50'
                          }`}
                        >

                          <p className="text-[11px]">
                            참가 후
                          </p>

                          <p className="mt-0.5 text-sm font-bold">
                            {formatSeed(
                              remainingPoint,
                            )}
                          </p>

                        </div>

                      </div>

                    </div>
                  );
                },
              )}

            </div>

          </div>

        </div>

      </div>
    );
  };


export default TournamentParticipantModal;